import { useQuery } from "@tanstack/react-query";
import type { VideoStatusResponse } from "../types/videoApi";
import { ApiError, apiGet } from "../lib/api";
import { REFETCH_INTERVAL_MS } from "../lib/constants";

/**
 * 영상분석 lot 의 구역 상태를 실제 백엔드에서 폴링한다.
 *
 * <p>{@code GET /api/video-status/{lotId}} — 중앙(Spring) 캐시의 최신 스냅샷. 현장 GPU 가
 * push 하지 않아 캐시가 비면 백엔드가 404 를 주는데, 이는 "표시할 영상분석 없음"이므로
 * null 로 처리한다(에러 아님 → BridgeRow 가 기존 층 표시로 자연 폴백).</p>
 *
 * <p>활성화 조건은 호출 측에서 {@code BridgeStatusDto.hasVideoAnalysis} 로 전달
 * (= 백엔드 영상 캐시에 lot 이 등록된 상태). 미활성 lot 에는 요청 자체가 안 나가서
 * 404 폭탄/로그 노이즈가 없다. 기존 FE 화이트리스트({@code VIDEO_ANALYSIS_LOT_IDS}) 대체.</p>
 *
 * <p>"N초째 불일치" 경과 표시는 ZoneList 가 generatedAt 기준으로 매초 계산하므로 폴링은 30초면 충분.</p>
 */
export function useVideoStatus(lotId: string, enabled: boolean) {
  return useQuery<VideoStatusResponse | null>({
    queryKey: ["video-status", lotId],
    queryFn: async ({ signal }) => {
      try {
        return await apiGet<VideoStatusResponse>(
          `/api/video-status/${lotId}`,
          signal ? { signal } : undefined,
        );
      } catch (e) {
        // 미수신(404) = 표시할 영상분석 데이터 없음 → null (정상 폴백, 에러 아님).
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
    placeholderData: (prev) => prev,
  });
}
